import React from 'react'
import {
  Popover as HPopover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react'
import styled from 'styled-components'
import { nanoid } from 'nanoid'

type PopoverProps = Parameters<typeof HPopover>[0]
type PopoverButtonProps = Parameters<typeof PopoverButton>[0]
type PopoverPanelProps = Parameters<typeof PopoverPanel>[0]

const TypedPopover = (props: PopoverProps) => <HPopover as="div" {...props} />
const TypedButton = (props: PopoverButtonProps) => (
  <PopoverButton as="button" {...props} />
)
const TypedPanel = (props: PopoverPanelProps) => (
  <PopoverPanel as="div" {...props} />
)

const StyledTypedListbox = styled(TypedPopover)<{ $isOpen?: boolean }>`
  position: relative;
  font-size: var(--default-text);
  font-family: ${({ theme }) => theme.fonts.primary};
`

const StyledPanel = styled(TypedPanel)<{ $offset?: number }>`
  z-index: 1;
  position: absolute;
  top: 100%;
  right: ${({ $offset }) => ($offset ? `${$offset}px` : 0)};
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.button.secondaryBorder};
  border-radius: 10px 0 10px 10px;
  padding: 16px 16px 16px 8px;

  display: flex;
  flex-direction: column;
  gap: 5px;
`

const StyledPopoverButton = styled(TypedButton)`
  font-size: var(--default-text);
  font-family: ${({ theme }) => theme.fonts.primary};
  box-sizing: border-box;
  border: none;
  width: 100%;
  background-color: transparent;
  text-align: left;
`

function getOffset(): number | undefined {
  const button = document.getElementById('PopoverButton__offset')
  return button?.offsetWidth
}

interface Props extends PopoverProps {
  className?: string
  label?: React.ReactElement
  elements: React.ReactElement[]
}

export const Popover: React.FC<Props> = ({ className, elements, label }) => {
  return (
    <>
      <StyledTypedListbox className={className}>
        {({ open, close }) => (
          <>
            <StyledPopoverButton id="PopoverButton__offset">
              {label}
            </StyledPopoverButton>
            {open && (
              <StyledPanel $offset={getOffset()}>
                {elements.map((element) => {
                  const id = nanoid()
                  return cloneElement(element, id, close)
                })}
              </StyledPanel>
            )}
          </>
        )}
      </StyledTypedListbox>
    </>
  )
}

function cloneElement(
  node: React.ReactElement,
  key: string,
  handler: () => void,
) {
  if (
    React.isValidElement<{
      onClick?: React.MouseEventHandler<HTMLAnchorElement>
    }>(node)
  ) {
    return React.cloneElement(node, {
      key: key,
      onClick: () => handler(),
    })
  }
  return node
}
